// A visitor who is not logged in: what the browser keeps for him.

const INSTALL_ID_KEY = 'installId';
const AUTH_REDIRECT_KEY = 'authRedirect';

// The id of this browser for the guest feed (the server cache key): 24 hex chars made here, kept until the signup.
const createInstallId = () => {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const getInstallId = () => {
  try {
    const stored = localStorage.getItem(INSTALL_ID_KEY);
    if (stored) return stored;
  } catch (e) {
    // Blocked storage: a new id below.
  }
  const installId = createInstallId();
  try {
    localStorage.setItem(INSTALL_ID_KEY, installId);
  } catch (e) {
    // Blocked storage: a new id at the next call.
  }
  return installId;
};

// IANA time zone of the browser (ex: Europe/Paris), as the app sends with its install. The webapp
// doesn't create installs for now: ready for the day it does.
const getDeviceTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {
    return '';
  }
};

// Page to open after the login (ex: the video of the feed where the popup was opened).
const saveAuthRedirect = (path: string) => {
  try {
    sessionStorage.setItem(AUTH_REDIRECT_KEY, path);
  } catch (e) {
    // Blocked storage: the user lands on his home page.
  }
};

const popAuthRedirect = () => {
  try {
    const path = sessionStorage.getItem(AUTH_REDIRECT_KEY);
    sessionStorage.removeItem(AUTH_REDIRECT_KEY);
    return path || '';
  } catch (e) {
    return '';
  }
};

const GUEST_VIDEOS_KEY = 'guestVideos';
const INVITE_SHOWN_KEY = 'inviteShown';
const INVITE_NEXT_KEY = 'inviteNext';
const INVITE_DAY_KEY = 'inviteDay';

const FIRST_INVITE_AT = 5;
const INVITE_EVERY = 10;

// In memory only: back to 0 when the page is loaded again.
let videosSincePageLoad = 0;

const readNumber = (key: string) => {
  try {
    return Number(localStorage.getItem(key)) || 0;
  } catch (e) {
    return 0;
  }
};

const write = (key: string, value: string | number) => {
  try {
    localStorage.setItem(key, String(value));
  } catch (e) {
    // Blocked storage: the counter starts again on the next visit.
  }
};

const getGuestVideos = () => readNumber(GUEST_VIDEOS_KEY);

const getInviteShown = () => readNumber(INVITE_SHOWN_KEY);

// A video watched without an account, added up across the visits: true when the invitation to
// sign up is due. First at the 5th video, then 10 videos after the last one, never twice a day.
const countGuestVideo = () => {
  const total = getGuestVideos() + 1;
  write(GUEST_VIDEOS_KEY, total);
  videosSincePageLoad += 1;

  const next = readNumber(INVITE_NEXT_KEY) || FIRST_INVITE_AT;
  let inviteDay = '';
  try {
    inviteDay = localStorage.getItem(INVITE_DAY_KEY) || '';
  } catch (e) {
    return false;
  }
  // Never on the first video of the visit: a popup right at the opening would push away.
  return total >= next && videosSincePageLoad >= 2 && inviteDay !== new Date().toDateString();
};

const saveInviteShown = () => {
  write(INVITE_SHOWN_KEY, getInviteShown() + 1);
  write(INVITE_NEXT_KEY, getGuestVideos() + INVITE_EVERY);
  write(INVITE_DAY_KEY, new Date().toDateString());
};

export {
  getDeviceTimezone,
  countGuestVideo,
  getGuestVideos,
  getInstallId,
  getInviteShown,
  popAuthRedirect,
  saveAuthRedirect,
  saveInviteShown,
};
